import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import keycloak from '../keycloak';
import { Chauffeur } from '../types';
import { authAPI } from '../services/api';

export interface KeycloakUser {
  subject: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName: string;
  roles: string[];
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  keycloakUser: KeycloakUser | null;
  chauffeur: Chauffeur | null;
  telephone: string;
  error: string | null;
  login: () => void;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [keycloakUser, setKeycloakUser] = useState<KeycloakUser | null>(null);
  const [chauffeur, setChauffeur] = useState<Chauffeur | null>(null);
  const [telephone, setTelephone] = useState('');
  const [error, setError] = useState<string | null>(null);

  const extractUserFromToken = useCallback((): KeycloakUser | null => {
    const parsed = keycloak.tokenParsed as any;
    if (!parsed) return null;

    const roles: string[] = [];
    if (parsed.realm_access && Array.isArray(parsed.realm_access.roles)) {
      roles.push(...parsed.realm_access.roles);
    }

    const firstName = parsed.given_name || '';
    const lastName = parsed.family_name || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ') || parsed.preferred_username || 'Utilisateur';

    return {
      subject: parsed.sub || '',
      username: parsed.preferred_username || '',
      email: parsed.email || '',
      firstName,
      lastName,
      fullName,
      roles,
    };
  }, []);

  const loadChauffeurProfile = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      if (response.success && response.data) {
        setChauffeur(response.data);
      }
    } catch (err) {
      console.warn('Impossible de charger le profil chauffeur métier:', err);
    }
  }, []);

  useEffect(() => {
    if ((keycloak as any)._initCalled) return;
    (keycloak as any)._initCalled = true;

    keycloak
      .init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      })
      .then((authenticated) => {
        setIsLoading(false);
        if (authenticated && keycloak.tokenParsed) {
          setIsAuthenticated(true);
          const user = extractUserFromToken();
          setKeycloakUser(user);
          const phone = user?.username || '';
          setTelephone(phone);
          loadChauffeurProfile();
        }
      })
      .catch((err) => {
        console.error('Erreur initialisation Keycloak:', err);
        setIsLoading(false);
        setError('Échec de la connexion. Veuillez réessayer.');
      });

    // Rafraîchissement automatique du token toutes les 60s
    const refreshInterval = setInterval(() => {
      if (keycloak.authenticated) {
        keycloak
          .updateToken(70)
          .then((refreshed) => {
            if (refreshed) {
              console.log('Token Keycloak rafraîchi');
            }
          })
          .catch(() => {
            console.error('Échec du refresh token, déconnexion');
            keycloak.logout({ redirectUri: window.location.origin });
          });
      }
    }, 60000);

    return () => clearInterval(refreshInterval);
  }, [extractUserFromToken, loadChauffeurProfile]);

  const login = useCallback(() => {
    keycloak.login({ redirectUri: window.location.origin });
  }, []);

  const logout = useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  const refreshProfile = useCallback(async () => {
    await loadChauffeurProfile();
  }, [loadChauffeurProfile]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-500 to-green-700">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Connexion sécurisée en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        keycloakUser,
        chauffeur,
        telephone,
        error,
        login,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth doit être utilisé à l\'intérieur de AuthProvider');
  }
  return ctx;
};
